'use client';

import { useLocalStorageState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useMemo, useState } from 'react';
import { ProductLineApply1 } from '@/app/components/icons/ProductLineApply1';
import { BackfillTargetOption } from '@/app/components/shared/BackfillTargetOption';
import { Button } from '@/app/components/ui/button';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@/app/components/ui/hover-card';
import { toast } from '@/app/components/ui/use-toast';
import { KEEP_CONTINUE_CREATE_PROMPT_STORAGE_KEY } from '@/app/constants';
import { useDevice, useRecreate } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import {
  BackfillTaskType,
  type FormImagePrompt,
  type TaskInfo,
  type TaskInputPrompts,
  type ViduV1Element,
} from '@/app/types';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import {
  getReferenceBackfillSettings,
  isReferenceBackfillTarget,
  type ReferenceBackfillTarget,
} from './CharacterContinueCreateButton.utils';

interface CharacterContinueCreateButtonProps {
  element: ViduV1Element;
  prompt?: string;
  showTriggerLabel?: boolean;
  triggerClassName?: string;
}

const TARGET_PATH_MAP: Partial<Record<BackfillTaskType, string>> = {
  [BackfillTaskType.CHARACTER2VIDEO]: '/create/character2video',
  [BackfillTaskType.REFERENCE2IMAGE]: '/create/reference2image',
  [BackfillTaskType.STORY_GRID]: '/create/story-grid',
  [BackfillTaskType.MOTION_CONTROL]: '/create/motion-control',
};

const APPLY_TYPE_MAP: Partial<Record<BackfillTaskType, string>> = {
  [BackfillTaskType.CHARACTER2VIDEO]: 'reference2video',
  [BackfillTaskType.REFERENCE2IMAGE]: 'reference2image',
  [BackfillTaskType.STORY_GRID]: 'storyGrid',
  [BackfillTaskType.MOTION_CONTROL]: 'motionControl',
};

export const CharacterContinueCreateButton: FC<CharacterContinueCreateButtonProps> = ({
  element,
  prompt = '',
  showTriggerLabel = false,
  triggerClassName,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const { handleRecreate } = useRecreate();
  const { supportsHover } = useDevice();
  const [selectedType, setSelectedType] = useState(BackfillTaskType.CHARACTER2VIDEO);
  const [keepPrompt = true, setKeepPrompt] = useLocalStorageState<boolean>(KEEP_CONTINUE_CREATE_PROMPT_STORAGE_KEY, {
    defaultValue: true,
    listenStorageChange: true,
  });
  const [isNonHoverCardOpen, setIsNonHoverCardOpen] = useState(false);

  const { updateMotionControlFormParams } = useStore((state) => ({
    updateMotionControlFormParams: state.updateMotionControlFormParams,
  }));

  const options = useMemo(
    () => [
      { label: t('create.tabCharacter2Video'), key: BackfillTaskType.CHARACTER2VIDEO },
      { label: t('create.tabReference2Image'), key: BackfillTaskType.REFERENCE2IMAGE },
      { label: t('tools.storyGrid.title'), key: BackfillTaskType.STORY_GRID },
      { label: t('tools.motionControl.title'), key: BackfillTaskType.MOTION_CONTROL },
    ],
    [t],
  );

  const textPrompt = keepPrompt ? prompt.trim() : '';

  const applyReferenceTarget = (target: ReferenceBackfillTarget) => {
    const taskType: TaskInfo['type'] = target === BackfillTaskType.STORY_GRID ? 'short_story' : target;
    const prompts = [
      {
        content: textPrompt,
        type: 'text' as TaskInputPrompts['type'],
      },
      {
        content: element.components?.map((item) => item.content),
        material: {
          id: element.id || '',
          share_id: element.share_meta?.id,
          version: element.version || '',
        },
        name: element.name,
        type: 'material' as TaskInputPrompts['type'],
      },
    ] as TaskInputPrompts[];

    handleRecreate({
      creationId: '',
      input: {
        enhance: true,
        prompts,
      },
      settings: {
        ...getReferenceBackfillSettings(target, element.modality),
      },
      taskId: '',
      taskType,
    });
  };

  const applyMotionControlTarget = () => {
    const component = element.components?.find((item) => item.content_type !== 'video' && item.content);
    if (!component?.content) {
      toast({ type: 'warning', message: t('frameShotDialog.backfillFailed') });
      return false;
    }

    const imagePrompt: FormImagePrompt = {
      id: `character-element-${element.id}`,
      status: 'done',
      material: {
        id: element.id || '',
        name: element.name,
        share_id: element.share_meta?.id,
        version: element.version || '',
      },
      prompt: {
        sourceImage: component.content,
        sourceImagePreview: component.content,
        selectedImage: component.content,
        selectedImagePreview: component.content,
      },
    };

    updateMotionControlFormParams('imagePrompts', [imagePrompt]);
    return true;
  };

  const handleApply = (target: BackfillTaskType) => {
    setSelectedType(target);
    if (!supportsHover) {
      setIsNonHoverCardOpen(false);
    }

    if (target === BackfillTaskType.MOTION_CONTROL) {
      if (!applyMotionControlTarget()) return;
    } else if (isReferenceBackfillTarget(target)) {
      applyReferenceTarget(target);
    } else {
      return;
    }

    reportEvent('apply_click', {
      apply_type: APPLY_TYPE_MAP[target] || '',
      apply_position: 'character_creation_task_history',
    });
    if (target === BackfillTaskType.MOTION_CONTROL) {
      router.push(TARGET_PATH_MAP[target] || '/create/motion-control');
    }
  };

  const handleTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!supportsHover) {
      setIsNonHoverCardOpen((prev) => !prev);
    }
  };

  return (
    <HoverCard
      openDelay={0}
      closeDelay={100}
      open={supportsHover ? undefined : isNonHoverCardOpen}
      onOpenChange={supportsHover ? undefined : setIsNonHoverCardOpen}
    >
      <HoverCardTrigger asChild>
        <Button
          variant="text"
          size="original"
          className={
            triggerClassName ||
            'size-8 rounded-full bg-system-white08 p-2.5 transition-colors hover:bg-system-white16 sm:h-8 sm:w-8 sm:p-2'
          }
          onClick={handleTriggerClick}
        >
          <ProductLineApply1 className="text-base" />
          {showTriggerLabel && <span>{t('task.action.continueCreate')}</span>}
        </Button>
      </HoverCardTrigger>
      <HoverCardPortal container={document.body}>
        <HoverCardContent
          className="w-fit bg-system-bg02 p-3 text-sm"
          align="center"
          sideOffset={2}
          side="top"
          onClick={(event) => event.stopPropagation()}
        >
          <BackfillTargetOption
            selectedType={selectedType}
            options={options}
            onApply={handleApply}
            showKeepPromptToggle={!!prompt.trim()}
            keepPrompt={keepPrompt}
            onKeepPromptChange={setKeepPrompt}
            titleText={t('task.action.continueCreate')}
          />
          <HoverCardArrow />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
